import React, { useState, useEffect, useCallback } from 'react';
import { lunchCouponsApi, configApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MultiSelect } from '../components/MultiSelect';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Search, Loader2, UtensilsCrossed, Save, BarChart3, ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const LunchCouponPage = () => {
  const { userBranches, isAdmin } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [branchFilter, setBranchFilter] = useState([]);
  const [classFilter, setClassFilter] = useState([]);
  const [search, setSearch] = useState('');
  const [records, setRecords] = useState([]);
  const [changes, setChanges] = useState({});

  // Report state
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportFromDate, setReportFromDate] = useState('');
  const [reportToDate, setReportToDate] = useState('');
  const [reportBranch, setReportBranch] = useState('all');
  const [reportClass, setReportClass] = useState('all');

  // Get available branches for this user
  const availableBranches = isAdmin ? (config?.branches || []) : userBranches;

  useEffect(() => {
    configApi.get().then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date };
      if (branchFilter.length) params.branch = branchFilter.join(',');
      if (classFilter.length) params.current_class = classFilter.join(',');
      const res = await lunchCouponsApi.getAll(params);
      setRecords(res.data.records);
      setChanges({});
    } catch (err) {
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [date, branchFilter, classFilter]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const toggleCoupon = (studentId, currentVal) => {
    setChanges(prev => ({ ...prev, [studentId]: !currentVal }));
  };

  const getUsedValue = (record) => {
    if (record.student_id in changes) return changes[record.student_id];
    return record.used;
  };

  const handleSave = async () => {
    const changedRecords = Object.entries(changes).map(([student_id, used]) => ({
      student_id, used
    }));
    if (changedRecords.length === 0) {
      toast.info('No changes to save');
      return;
    }
    setSaving(true);
    try {
      await lunchCouponsApi.bulkMark({ date, records: changedRecords });
      toast.success(`Saved ${changedRecords.length} coupon records`);
      setChanges({});
      fetchRecords();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const markAll = (value) => {
    const filtered = getFilteredRecords();
    const newChanges = { ...changes };
    filtered.forEach(r => { newChanges[r.student_id] = value; });
    setChanges(newChanges);
  };

  const getFilteredRecords = () => {
    if (!search) return records;
    const s = search.toLowerCase();
    return records.filter(r =>
      r.first_name.toLowerCase().includes(s) ||
      r.last_name.toLowerCase().includes(s) ||
      r.student_id.toLowerCase().includes(s)
    );
  };

  const fetchReport = async () => {
    if (!reportFromDate || !reportToDate) {
      toast.error('Please select both dates');
      return;
    }
    setReportLoading(true);
    try {
      const params = { from_date: reportFromDate, to_date: reportToDate };
      if (reportBranch !== 'all') params.branch = reportBranch;
      if (reportClass !== 'all') params.current_class = reportClass;
      const res = await lunchCouponsApi.report(params);
      setReportData(res.data);
    } catch (err) {
      toast.error('Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  };

  const changeDate = (offset) => {
    const d = new Date(date);
    d.setDate(d.getDate() + offset);
    setDate(d.toISOString().split('T')[0]);
  };

  const filtered = getFilteredRecords();
  const usedCount = filtered.filter(r => getUsedValue(r)).length;
  const hasChanges = Object.keys(changes).length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1F1D]" data-testid="lunch-coupon-title">Lunch Coupons</h1>
          <p className="text-[#5C635F] mt-1">Track daily lunch coupon usage by students</p>
        </div>
      </div>

      <Tabs defaultValue="tracker" className="space-y-4">
        <TabsList data-testid="lunch-tabs">
          <TabsTrigger value="tracker" data-testid="lunch-tracker-tab">
            <UtensilsCrossed className="w-4 h-4 mr-2" />Daily Tracker
          </TabsTrigger>
          <TabsTrigger value="report" data-testid="lunch-report-tab">
            <BarChart3 className="w-4 h-4 mr-2" />Report
          </TabsTrigger>
        </TabsList>

        {/* ======= TRACKER TAB ======= */}
        <TabsContent value="tracker" className="space-y-4">
          <Card className="border-[#D8D9E8]">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => changeDate(-1)} data-testid="prev-date-btn">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-44" data-testid="coupon-date-input" />
                  <Button variant="outline" size="sm" onClick={() => changeDate(1)} data-testid="next-date-btn">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
                <MultiSelect widthClass="w-40" placeholder="All Branches" testId="coupon-branch-filter"
                  options={availableBranches || []} selected={branchFilter} onChange={v => setBranchFilter(v)} />
                <MultiSelect widthClass="w-40" placeholder="All Classes" testId="coupon-class-filter"
                  options={config?.classes || []} selected={classFilter} onChange={v => setClassFilter(v)} />
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C635F]" />
                  <Input placeholder="Search student..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="coupon-search" />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Badge variant="secondary" className="bg-[#ECEDF5] text-[#3E3F96]" data-testid="coupon-used-count">
                {usedCount} / {filtered.length} used
              </Badge>
              <Button variant="outline" size="sm" onClick={() => markAll(true)} data-testid="mark-all-used-btn">Mark All Used</Button>
              <Button variant="outline" size="sm" onClick={() => markAll(false)} data-testid="mark-all-unused-btn">Clear All</Button>
            </div>
            <Button onClick={handleSave} disabled={saving || !hasChanges} className="bg-[#3E3F96] hover:bg-[#2D2E7A]" data-testid="save-coupons-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes {hasChanges && `(${Object.keys(changes).length})`}
            </Button>
          </div>

          <Card className="border-[#D8D9E8]">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-48"><Loader2 className="w-8 h-8 animate-spin text-[#3E3F96]" /></div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F5F5FA] hover:bg-[#F5F5FA]">
                        <TableHead className="text-xs uppercase font-semibold text-[#5C635F] w-16">Used</TableHead>
                        <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Student ID</TableHead>
                        <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Name</TableHead>
                        <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Class</TableHead>
                        <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Branch</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map(r => {
                        const used = getUsedValue(r);
                        const changed = r.student_id in changes;
                        return (
                          <TableRow key={r.student_id} className={changed ? 'bg-[#3E3F96]/5' : ''} data-testid={`coupon-row-${r.student_id}`}>
                            <TableCell>
                              <Checkbox
                                checked={used}
                                onCheckedChange={() => toggleCoupon(r.student_id, used)}
                                data-testid={`coupon-check-${r.student_id}`}
                              />
                            </TableCell>
                            <TableCell className="font-medium text-[#3E3F96]">{r.student_id}</TableCell>
                            <TableCell className="text-[#1C1F1D]">{r.first_name} {r.last_name}</TableCell>
                            <TableCell><Badge variant="secondary" className="bg-[#ECEDF5] text-[#3E3F96]">{r.current_class}</Badge></TableCell>
                            <TableCell className="text-[#5C635F]">{r.branch}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {filtered.length === 0 && (
                    <div className="flex items-center justify-center h-32 text-[#5C635F]">No students found</div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ======= REPORT TAB ======= */}
        <TabsContent value="report" className="space-y-4">
          <Card className="border-[#D8D9E8]">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="space-y-1">
                  <Label className="text-xs text-[#5C635F]">From Date</Label>
                  <Input type="date" value={reportFromDate} onChange={e => setReportFromDate(e.target.value)} data-testid="report-from-date" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#5C635F]">To Date</Label>
                  <Input type="date" value={reportToDate} onChange={e => setReportToDate(e.target.value)} data-testid="report-to-date" />
                </div>
                <Select value={reportBranch} onValueChange={setReportBranch}>
                  <SelectTrigger className="w-40" data-testid="report-branch-filter"><SelectValue placeholder="Branch" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {config?.branches?.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={reportClass} onValueChange={setReportClass}>
                  <SelectTrigger className="w-40" data-testid="report-class-filter"><SelectValue placeholder="Class" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Classes</SelectItem>
                    {config?.classes?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={fetchReport} disabled={reportLoading} className="bg-[#3E3F96] hover:bg-[#2D2E7A]" data-testid="generate-report-btn">
                  {reportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
                  Generate Report
                </Button>
              </div>
            </CardContent>
          </Card>

          {reportData && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card className="border-[#D8D9E8]"><CardContent className="p-4 text-center">
                  <p className="text-xs uppercase text-[#5C635F]">Students</p>
                  <p className="text-2xl font-bold text-[#1C1F1D]" data-testid="report-total-students">{reportData.summary.total_students}</p>
                </CardContent></Card>
                <Card className="border-[#D8D9E8]"><CardContent className="p-4 text-center">
                  <p className="text-xs uppercase text-[#5C635F]">School Days</p>
                  <p className="text-2xl font-bold text-[#1C1F1D]" data-testid="report-total-days">{reportData.summary.total_school_days}</p>
                </CardContent></Card>
                <Card className="border-[#D8D9E8]"><CardContent className="p-4 text-center">
                  <p className="text-xs uppercase text-[#5C635F]">Coupons Used</p>
                  <p className="text-2xl font-bold text-[#3E3F96]" data-testid="report-coupons-used">{reportData.summary.total_coupons_used}</p>
                </CardContent></Card>
                <Card className="border-[#D8D9E8]"><CardContent className="p-4 text-center">
                  <p className="text-xs uppercase text-[#5C635F]">Usage Rate</p>
                  <p className="text-2xl font-bold text-[#3E3F96]" data-testid="report-usage-rate">{reportData.summary.overall_percentage}%</p>
                </CardContent></Card>
              </div>

              <Card className="border-[#D8D9E8]">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#F5F5FA] hover:bg-[#F5F5FA]">
                          <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Student ID</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Name</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Class</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Branch</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-center">Days Used</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-center">Total Days</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-center">Usage %</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reportData.report.map(r => (
                          <TableRow key={r.student_id} data-testid={`report-row-${r.student_id}`}>
                            <TableCell className="font-medium text-[#3E3F96]">{r.student_id}</TableCell>
                            <TableCell>{r.first_name} {r.last_name}</TableCell>
                            <TableCell><Badge variant="secondary" className="bg-[#ECEDF5] text-[#3E3F96]">{r.current_class}</Badge></TableCell>
                            <TableCell className="text-[#5C635F]">{r.branch}</TableCell>
                            <TableCell className="text-center font-medium">{r.days_used}</TableCell>
                            <TableCell className="text-center text-[#5C635F]">{r.total_days}</TableCell>
                            <TableCell className="text-center">
                              <Badge className={r.percentage >= 75 ? 'bg-green-100 text-green-700' : r.percentage >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-600'}>
                                {r.percentage}%
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LunchCouponPage;
